import React from "react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Download } from "lucide-react";
import { toast } from "sonner";

/**
 * ExportMenu — small dropdown for CSV / Excel exports of a list endpoint.
 *
 * Props:
 *  - resource: backend resource slug, e.g. "defaulters" → calls /api/exports/defaulters
 *  - extraParams: object of additional querystring params to forward (current filters)
 *  - filename: optional base filename (without extension)
 *  - testId: optional data-testid override
 *  - label: optional label override (default "Export")
 */
export default function ExportMenu({ resource, extraParams = {}, filename, testId, label = "Export" }) {
  const triggerId = testId || `export-${resource}-trigger`;

  const doExport = async (format) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const token = localStorage.getItem("eata.token");
    const qs = new URLSearchParams({ format, ...extraParams });
    toast.message(`Preparing ${format.toUpperCase()} export…`);
    try {
      const resp = await fetch(`${base}/api/exports/${resource}?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const t = await resp.text();
        toast.error(`Export failed (${resp.status})`);
        console.error(t);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename || resource}.${format === "xlsx" ? "xlsx" : "csv"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${a.download}`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" data-testid={triggerId} aria-label={label}>
          <Download className="h-4 w-4 mr-1" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => doExport("csv")} data-testid={`${triggerId}-csv`}>
          CSV (.csv)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid={`${triggerId}-xlsx`}>
          Excel (.xlsx)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
